import React from 'react';
import { motion } from 'framer-motion';

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 12 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.4, delay, ease: [0.22, 1, 0.36, 1] },
});

const STACK = [
  {
    category: 'Languages',
    items: [
      { name: 'JavaScript', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg' },
      { name: 'TypeScript', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/typescript/typescript-original.svg' },
      { name: 'Python',     logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg' },
      { name: 'C++',        logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/cplusplus/cplusplus-original.svg' },
      { name: 'C',          logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/c/c-original.svg' },
    ],
  },
  {
    category: 'Frontend',
    items: [
      { name: 'React',        logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg' },
      { name: 'Next.js',      logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nextjs/nextjs-original.svg' },
      { name: 'Tailwind CSS', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/tailwindcss/tailwindcss-original.svg' },
      { name: 'HTML5',        logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/html5/html5-original.svg' },
      { name: 'CSS3',         logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/css3/css3-original.svg' },
      { name: 'Framer Motion',logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/framermotion/framermotion-original.svg' },
    ],
  },
  {
    category: 'Backend',
    items: [
      { name: 'Node.js',    logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg' },
      { name: 'Express.js', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/express/express-original.svg' },
      { name: 'NestJS',     logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/nestjs/nestjs-original.svg' },
      { name: 'FastAPI',    logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/fastapi/fastapi-original.svg' },
      { name: 'Socket.io',  logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/socketio/socketio-original.svg' },
      { name: 'PostgreSQL', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/postgresql/postgresql-original.svg' },
      { name: 'MongoDB',    logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mongodb/mongodb-original.svg' },
      { name: 'Redis',      logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/redis/redis-original.svg' },
      { name: 'MySQL',      logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mysql/mysql-original.svg' },
    ],
  },
  {
    category: 'AI & Infra',
    items: [
      { name: 'LangChain', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/langchain/langchain-original.svg' },
      { name: 'OpenAI',    logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/openai/openai-original.svg' },
      { name: 'Docker',    logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg' },
      { name: 'AWS',       logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-plain-wordmark.svg' },
      { name: 'Git',       logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/git/git-original.svg' },
      { name: 'Linux',     logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/linux/linux-original.svg' },
      { name: 'Vercel',    logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/vercel/vercel-original.svg' },
      { name: 'Postman',   logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/postman/postman-original.svg' },
    ],
  },
];

const TOTAL = STACK.reduce((a, g) => a + g.items.length, 0);

function TechItem({ item, delay }) {
  return (
    <motion.div
      {...fadeUp(delay)}
      className="flex items-center gap-2.5 py-2 px-3 rounded-xl bg-white border border-black/8 hover:border-purple-300 hover:shadow-[2px_2px_0px_0px_rgba(124,58,237,0.15)] transition-all duration-200 group cursor-default"
    >
      <img
        src={item.logo}
        alt={item.name}
        className="w-5 h-5 object-contain shrink-0 group-hover:scale-110 transition-transform duration-200"
        onError={e => { e.target.style.display = 'none'; }}
      />
      <span className="text-[12px] font-semibold text-black/70 group-hover:text-black transition-colors duration-200 whitespace-nowrap">
        {item.name}
      </span>
    </motion.div>
  );
}

export default function TechStack() {
  return (
    <section id="techstack" className="relative w-full overflow-hidden bg-transparent py-8 lg:py-10">
      <div className="relative z-10 max-w-3xl mx-auto px-6 sm:px-10 lg:px-14">

        {/* Header */}
        <div className="flex items-end justify-between mb-6">
          <div>
            <motion.p {...fadeUp(0)} className="font-mono text-[10px] uppercase tracking-[0.2em] text-purple-600 font-bold mb-1">
              &lt;/Stack&gt;
            </motion.p>
            <motion.h1 {...fadeUp(0.04)} className="text-2xl sm:text-3xl font-black text-black">
              Tech Stack
            </motion.h1>
          </div>
          <motion.div {...fadeUp(0.08)} className="pb-0.5 text-right">
            <span className="text-lg font-black text-black leading-none block">{TOTAL}</span>
            <span className="text-[9px] font-mono text-black/30 uppercase tracking-wider mt-0.5 block">technologies</span>
          </motion.div>
        </div>

        <motion.div {...fadeUp(0.1)} className="h-px bg-black/10 mb-6" />

        {/* Groups */}
        <div className="space-y-6">
          {STACK.map((group, gi) => (
            <div key={group.category}>
              <motion.p
                {...fadeUp(0.1 + gi * 0.04)}
                className="text-[9px] font-mono uppercase tracking-widest text-black/30 mb-2.5"
              >
                {group.category}
              </motion.p>
              <div className="flex flex-wrap gap-2">
                {group.items.map((item, ii) => (
                  <TechItem
                    key={item.name}
                    item={item}
                    delay={0.12 + gi * 0.04 + ii * 0.025}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}